import React, { useMemo } from 'react';
import {
  arrayOf, string, exact, number, bool,
} from 'prop-types';
import { useHistory } from 'react-router-dom';
import styled, { css } from 'styled-components';
import {
  Button, Flex, Typography, Icon,
} from '@atoms';
import { routes } from '@constants';
import { ReactComponent as Edit } from '@assets/edit.svg';

const MenuHeader = ({
  isSubcategoryBarVisible,
  categories,
  subcategories,
  selectedCategoryId,
}) => {
  const history = useHistory();

  const onCategorySelect = id => () => {
    history.push({
      pathname: routes.categoryEdit,
      state: {
        id,
      },
    });
  };

  const onSubcategorySelect = id => () => {
    history.push({
      pathname: routes.subcategoryEdit,
      state: {
        id,
        parentId: selectedCategoryId,
      },
    });
  };

  const onCategoryAdd = () => {
    history.push(routes.categoryEdit);
  };

  const onSubcategoryAdd = () => {
    history.push({
      pathname: routes.subcategoryEdit,
      state: {
        parentId: selectedCategoryId,
      },
    });
  };

  const onCategoriesOrderEdit = () => {
    history.push(routes.categoriesOrder);
  };

  const memoizedСategories = useMemo(() => (
    categories.map(({ title, id }) => (
      <CategoryButton
        key={id}
        id={`category-${id}`}
        isActive={id === selectedCategoryId}
        onClick={onCategorySelect(id)}
      >
        <Typography size="lg" weight="bold">
          {title}
        </Typography>
      </CategoryButton>
    ))
  ), [categories]);

  const memoizedSubcategories = useMemo(() => (
    subcategories.map(({ title, id }) => (
      <Button.Rectangular
        primary
        onClick={onSubcategorySelect(id)}
        key={id}
      >
        <Typography size="lg" weight="bold">
          {title}
        </Typography>
      </Button.Rectangular>
    ))
  ), [subcategories]);

  return (
    <Wrapper column>
      <Container id="categories-container" itemsCenter padding="md">
        <EditButton diameter={32} onClick={onCategoriesOrderEdit}>
          <Icon SVG={Edit} width="32px" height="13px" />
        </EditButton>
        <CategoryButton onClick={onCategoryAdd}>
          <Typography size="lg" weight="bold">
            +
          </Typography>
        </CategoryButton>
        {memoizedСategories}
      </Container>
      {isSubcategoryBarVisible && (
        <Container itemsCenter padding="md" subcategories>
          <Button.Rectangular
            primary
            onClick={onSubcategoryAdd}
            marginRight="xs"
          >
            <Typography size="lg" weight="bold">
              +
            </Typography>
          </Button.Rectangular>
          {memoizedSubcategories}
        </Container>
      )}
    </Wrapper>
  );
};

const CategoryButton = styled(Button.Rectangular)`
  ${({ isActive, theme: { color } }) => css`
    color: ${color.strawberry};

    ${isActive && `
      background-color: ${color.strawberry};
      color: ${color.white};
    `}
  `}
`;

const Wrapper = styled(Flex)`
  width: 100%;
  position: -webkit-sticky;
  position: sticky;
  top: 44px;
`;

const Container = styled(Flex)`
  ${({
    subcategories,
    theme: { color, spacing },
  }) => css`
    display: inline-block;
    background-color: ${subcategories ? color.strawberry : color.white};
    overflow: auto;
    white-space: nowrap;

    -ms-overflow-style: none;  /* Hide scrollbar for IE and Edge */
    scrollbar-width: none; /* Hide scrollbar for Firefox */
    &::-webkit-scrollbar { /* Hide scrollbar for Chrome, Safari and Opera */
      display: none;
    }

    a:not(:last-of-type) {
      margin-right: ${spacing.sm};
    }

    ${Button.Rectangular} {
      padding: ${spacing.sm} ${spacing.md};

      &:not(:first-of-type) {
        margin-left: ${spacing.xs};
      }
    }
  `}
`;

const EditButton = styled(Button.Circular)`
  ${({ theme: { color, typography } }) => css`
    font-size: ${typography.size.lg};

    svg {
      path {
        fill: ${color.strawberry};
      }
    }
  `}
`;

MenuHeader.defaultProps = {
  selectedCategoryId: 0,
};

MenuHeader.propTypes = {
  isSubcategoryBarVisible: bool.isRequired,
  categories: arrayOf(exact({
    imageSrc: string,
    title: string,
    isActive: bool,
    id: number,
  })).isRequired,
  subcategories: arrayOf(exact({
    title: string,
    id: number,
  })).isRequired,
  selectedCategoryId: number,
};

export default MenuHeader;
